import { useSyncExternalStore } from 'react';

/**
 * Small links between the notification and badge (mounted in the app shell) and the live inbox page:
 *   - whether the inbox page is on screen in THIS tab, so the badge reads its list only while it is kept current;
 *   - the thread the inbox has open in THIS tab, so a new message in it raises no toast (it is being read);
 *   - a request to open a thread (the toast's Open), which the inbox applies whether it is already on screen or
 *     mounts next. `?conv=<id>` in the URL does the same on a reload or a pasted link.
 * Kept outside the router so the inbox (and its tests) need no router context.
 */
let pagesOpen = 0;
const pageListeners = new Set<() => void>();

/** Marks the inbox page as on screen until the returned cleanup runs. */
export function markInboxPageOpen(): () => void {
  pagesOpen += 1;
  pageListeners.forEach((l) => l());
  let done = false;
  return () => {
    if (done) return;
    done = true;
    pagesOpen -= 1;
    pageListeners.forEach((l) => l());
  };
}

function subscribePage(listener: () => void): () => void {
  pageListeners.add(listener);
  return () => pageListeners.delete(listener);
}

export function useInboxPageOpen(): boolean {
  return useSyncExternalStore(subscribePage, () => pagesOpen > 0, () => false);
}

let openThread: string | null = null;

export function setOpenInboxThread(conversationId: string | null): void {
  openThread = conversationId;
}

export function openInboxThread(): string | null {
  return openThread;
}

export interface ThreadRequest {
  conversationId: string;
  /** Grows with every request, so asking for the same thread twice still applies. */
  seq: number;
}

let request: ThreadRequest | null = null;
let seq = 0;
const listeners = new Set<() => void>();

export function requestInboxThread(conversationId: string): void {
  seq += 1;
  request = { conversationId, seq };
  listeners.forEach((l) => l());
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useInboxThreadRequest(): ThreadRequest | null {
  return useSyncExternalStore(subscribe, () => request, () => null);
}

/** The newest request an inbox has already applied: a later mount must not reopen it. */
let appliedSeq = 0;

export function markInboxThreadRequestApplied(requestSeq: number): void {
  if (requestSeq > appliedSeq) appliedSeq = requestSeq;
}

/** The request still waiting for an inbox to open it, or null. */
export function pendingInboxThreadRequest(current: ThreadRequest | null): ThreadRequest | null {
  return current && current.seq > appliedSeq ? current : null;
}

/** `?conv=<id>` from the address bar, or null. */
export function threadFromUrl(search: string = typeof window === 'undefined' ? '' : window.location.search): string | null {
  const id = new URLSearchParams(search).get('conv');
  return id && id.trim() ? id.trim() : null;
}
